import { Chip } from '@design/components/chip/Chip';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { DropdownEntry } from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { DropdownRadioGroup } from '@design/components/dropdown-menu/dropdown-radio-group/DropdownRadioGroup';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './CalendarReportingDates.module.scss';
import {
  CalendarReportingPeriod,
  getCalendarQuarterFromReportingDate,
} from './utils';

export type CalendarReportingDatesProps<T extends CalendarReportingPeriod> = {
  reportingPeriods: T[];
  activeReportingPeriod: T;
  onActiveReportingPeriodChange: (reportingPeriod: T) => void;
};

export const CalendarReportingDates = <T extends CalendarReportingPeriod>({
  reportingPeriods,
  activeReportingPeriod,
  onActiveReportingPeriodChange,
}: CalendarReportingDatesProps<T>) => {
  const { t } = useTranslation(['earnings']);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getTimeframeLabel = (reportingDate: string) => {
    const { startDate, endDate } =
      getCalendarQuarterFromReportingDate(reportingDate);
    return t('earnings:reportingDates.timeframe', { startDate, endDate });
  };

  const periodItems: DropdownEntry[] = reportingPeriods.map(reportingPeriod => {
    const { label } = getCalendarQuarterFromReportingDate(
      reportingPeriod.reporting_date
    );

    return {
      key: reportingPeriod.reporting_date,
      label,
      tooltip: getTimeframeLabel(reportingPeriod.reporting_date),
      onClick: () => {
        onActiveReportingPeriodChange(reportingPeriod);
        setIsDropdownOpen(false);
      },
    };
  });

  const activeQuarter = getCalendarQuarterFromReportingDate(
    activeReportingPeriod.reporting_date
  );
  const timeframeLabel = getTimeframeLabel(
    activeReportingPeriod.reporting_date
  );

  return (
    <div className={styles.root} data-testid="reporting-dates">
      <DropdownMenu
        open={isDropdownOpen}
        onOpenChange={setIsDropdownOpen}
        className={styles.menuWrapper}
        triggerNode={
          <Chip
            className={styles.chip}
            label={
              <Tooltip
                label={timeframeLabel}
                trigger={TooltipTrigger.Hover}
                disabled={isDropdownOpen}
              >
                <div className={styles.labelContainer}>
                  <span className={styles.label}>
                    {t('earnings:reportingDates.label')}
                  </span>
                  <span className={styles.activePeriod}>
                    {activeQuarter.label}
                  </span>
                </div>
              </Tooltip>
            }
            rightIcon="ChevronLightDown"
            rightIconSize={12}
            data-testid="reporting-dates-chip"
          />
        }
      >
        <DropdownRadioGroup
          items={periodItems}
          selectedKey={activeReportingPeriod.reporting_date}
          tooltipProps={{ placement: 'right' }}
        />
      </DropdownMenu>
    </div>
  );
};
